export const PLAN_DEFINITIONS = [
  {
    key: 'trial',
    title: 'Trial',
    label: 'Plan Trial',
    eyebrow: 'Prueba activa',
    detail:
      'Acceso inicial por 30 días para crear rutas, unidades de transporte, choferes, destinos y probar el trabajo real antes de pasar a un plan activo.',
    highlightTitle: 'Operación base disponible',
    highlightText:
      'Permite crear rutas, dar de alta unidades y choferes, ordenar contactos y usar accesos individuales para marcar destinos dentro de un contexto real de trabajo.',
    badge: 'Trial activo',
    modules: ['logistics', 'service', 'systemGuide'],
    items: [
      'Creación de rutas y hojas de trabajo',
      'Alta de unidades de transporte',
      'Alta de choferes y datos de contacto',
      'Acceso individual para cada chofer',
      'Marcación de destinos por chofer en tiempo real',
      'Base de clientes, contactos y direcciones frecuentes',
      'Sin tarjeta y con datos conservados al finalizar la prueba',
    ],
    summary: 'Entrada de 30 días para probar rutas, unidades, choferes, destinos y accesos individuales.',
  },
  {
    key: 'base',
    title: 'Base',
    label: 'Plan Base',
    eyebrow: 'Operación diaria ordenada',
    detail:
      'Pensado para empresas que necesitan organizar rutas, crear unidades, cargar choferes, guardar destinos frecuentes y dar acceso individual para marcar entregas y retiros.',
    highlightTitle: 'Base operativa centralizada',
    highlightText:
      'Guardá clientes, proveedores, contactos y direcciones frecuentes para crear destinos más rápido y sostener una operación ordenada por transporte y chofer.',
    badge: 'Operación diaria',
    modules: ['logistics', 'systemGuide'],
    items: [
      'Creación y gestión de rutas operativas',
      'Creación de unidades de transporte',
      'Creación de choferes y accesos individuales',
      'Cada chofer puede marcar destinos, entregas y retiros',
      'Base centralizada de clientes, proveedores, contactos y direcciones frecuentes',
      'GIA para acompañar implementación, uso y alcance del sistema',
      'Sin motor de stock avanzado',
    ],
    summary: 'La base comercial para operar todos los días con orden, continuidad y seguimiento individual por transporte.',
  },
  {
    key: 'intermedio',
    title: 'Intermedio',
    label: 'Plan Intermedio',
    eyebrow: 'Stock y monitoreo',
    detail:
      'Incluye todo lo del Plan Base y suma vista general de unidades en movimiento más control de stock para compras, salidas, cantidades y movimientos.',
    highlightTitle: 'Del seguimiento individual al stock operativo',
    highlightText:
      'Además del seguimiento individual por transporte, incorpora monitor global y una planilla viva para leer qué entró, qué salió, cuánto queda y cuándo se movió.',
    badge: 'Monitoreo en vivo',
    modules: ['stock', 'logistics', 'service', 'routeMonitor', 'systemGuide'],
    items: [
      'Todo lo incluido en el Plan Base',
      'Vista general de varias unidades al mismo tiempo',
      'Mapa con rutas y choferes activos',
      'Detalle por unidad, recorrido y estado operativo',
      'Control de productos, cantidades y categorías',
      'Registro de compras de mercadería',
      'Preparación de salidas de stock',
      'Planilla de movimientos: qué entró, qué salió y cuándo',
      'Control físico básico para comparar sistema contra depósito',
    ],
    summary: 'Suma lectura en vivo y convierte la operación en una vista centralizada.',
  },
  {
    key: 'avanzado',
    title: 'Avanzado',
    label: 'Plan Avanzado',
    eyebrow: 'Trazabilidad profunda',
    detail:
      'Incluye todo lo del Plan Intermedio y suma historia detallada de productos, garantías, devoluciones, reportes, gastos de flota y diferencia entre costo y venta.',
    highlightTitle: 'Historia completa del producto y la operación',
    highlightText:
      'Analizá cuándo entró un producto, cuánto costó, cuánto tiempo estuvo guardado, cuándo salió, quién lo llevó, a dónde fue y qué diferencia dejó.',
    badge: 'Control ampliado',
    modules: ['stock', 'logistics', 'service', 'routeMonitor', 'reports', 'fleetExpenses', 'systemGuide'],
    items: [
      'Todo lo incluido en el Plan Intermedio',
      'Historia por producto: entrada, costo, permanencia y salida',
      'Registro de destino, transporte, chofer y receptor',
      'Garantías, devoluciones e incidentes',
      'Diferencia entre costo de compra y precio de salida',
      'Balance general de la operación',
      'Configuración de costos por unidad o tipo de transporte',
      'Lectura consolidada del rendimiento operativo',
      'Bloque de gastos de flota para completar la visión económica',
      'Preparado para QR, código de barras, fotos y evidencia',
    ],
    summary: 'Integra operación, historia de productos, reportes y control económico en una sola capa de gestión.',
  },
];

export const ALWAYS_ON_MODULE_KEYS = new Set(['systemGuide']);
const ALL_PLAN_MODULE_KEYS = Array.from(
  new Set(
    PLAN_DEFINITIONS.flatMap((plan) => plan.modules || []).concat(Array.from(ALWAYS_ON_MODULE_KEYS))
  )
);

const DAY_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_TRIAL_DAYS = 30;
const TRIAL_EXPIRING_SOON_DAYS = 3;
const BILLING_EXPIRING_SOON_DAYS = 7;

const PLAN_ALIAS_MAP = {
  trial: 'trial',
  'plan trial': 'trial',
  prueba: 'trial',
  demo: 'trial',
  base: 'base',
  basico: 'base',
  básico: 'base',
  basic: 'base',
  intermedio: 'intermedio',
  intermediate: 'intermedio',
  monitor: 'intermedio',
  avanzado: 'avanzado',
  advanced: 'avanzado',
  premium: 'avanzado',
};

const PLAN_LOOKUP = PLAN_DEFINITIONS.reduce((acc, plan) => {
  acc[plan.key] = plan;
  return acc;
}, {});

export const normalizeComparableText = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const getPlanDefinition = (planKey = 'base') => PLAN_LOOKUP[planKey] || PLAN_LOOKUP.base;

export const resolvePlanKeyAlias = (value = '') => {
  const normalized = normalizeComparableText(value);
  if (!normalized) return null;

  if (PLAN_ALIAS_MAP[normalized]) return PLAN_ALIAS_MAP[normalized];

  const partialMatch = Object.keys(PLAN_ALIAS_MAP).find((candidate) => normalized.includes(candidate));
  return partialMatch ? PLAN_ALIAS_MAP[partialMatch] : null;
};

export const buildModulesSettingsFromPlanKey = (planKey = 'base') => {
  const plan = getPlanDefinition(planKey);
  const enabledKeys = new Set([...(plan?.modules || []), ...ALWAYS_ON_MODULE_KEYS]);

  return ALL_PLAN_MODULE_KEYS.reduce((acc, key) => {
    acc[key] = { enabled: enabledKeys.has(key) };
    return acc;
  }, {});
};

const detectPlanFromModules = (modulesSettings = {}) => {
  const normalizedEntries = ALL_PLAN_MODULE_KEYS.map((key) => ({
    key,
    enabled: ALWAYS_ON_MODULE_KEYS.has(key) ? true : modulesSettings?.[key]?.enabled !== false,
  }));

  const matched = PLAN_DEFINITIONS.find((plan) => {
    const enabledKeys = new Set([...(plan.modules || []), ...ALWAYS_ON_MODULE_KEYS]);
    return normalizedEntries.every((entry) => entry.enabled === enabledKeys.has(entry.key));
  });

  return matched?.key || null;
};

export const resolveCompanyPlanMeta = ({ generalSettings = {}, planSettings = {}, modulesSettings = {} } = {}) => {
  const candidates = [
    // Prioridad para los campos canónicos que hoy se escriben al cambiar el plan.
    planSettings.planId,
    planSettings.planKey,
    planSettings.subscriptionPlan,
    planSettings.currentPlanId,
    planSettings.subscriptionPlanName,
    planSettings.planName,
    planSettings.currentPlanName,
    planSettings.plan,

    // Fallback a generalSettings por compatibilidad con documentos previos.
    generalSettings.planId,
    generalSettings.planKey,
    generalSettings.subscriptionPlan,
    generalSettings.currentPlanId,
    generalSettings.subscriptionPlanName,
    generalSettings.planName,
    generalSettings.currentPlanName,
    generalSettings.plan,
  ];

  const explicitKey = candidates.map(resolvePlanKeyAlias).find(Boolean);
  if (explicitKey) {
    const plan = getPlanDefinition(explicitKey);
    return {
      key: plan.key,
      label: plan.label,
      title: plan.title,
      description: plan.summary,
      isCustom: false,
    };
  }

  const detectedKey = detectPlanFromModules(modulesSettings);
  if (detectedKey) {
    const plan = getPlanDefinition(detectedKey);
    return {
      key: plan.key,
      label: plan.label,
      title: plan.title,
      description: plan.summary,
      isCustom: false,
    };
  }

  const hasSignal = candidates.some((value) => normalizeComparableText(value));
  const hasEnabledModules = Object.values(modulesSettings || {}).some((value) => value?.enabled !== false);

  if (hasSignal || hasEnabledModules) {
    return {
      key: 'personalizado',
      label: 'Plan Personalizado',
      title: 'Personalizado',
      description: 'Combinación manual de módulos fuera de un preset comercial.',
      isCustom: true,
    };
  }

  const fallback = getPlanDefinition('base');
  return {
    key: fallback.key,
    label: fallback.label,
    title: fallback.title,
    description: fallback.summary,
    isCustom: false,
  };
};

export const resolveModulesSettingsWithPlanFallback = ({
  generalSettings = {},
  planSettings = {},
  modulesSettings = {},
} = {}) => {
  const planMeta = resolveCompanyPlanMeta({ generalSettings, planSettings, modulesSettings });
  const baseModules = buildModulesSettingsFromPlanKey(
    planMeta?.key && planMeta.key !== 'personalizado' ? planMeta.key : 'base'
  );

  const merged = { ...baseModules };

  Object.entries(modulesSettings || {}).forEach(([moduleKey, moduleValue]) => {
    merged[moduleKey] = {
      ...(baseModules[moduleKey] || { enabled: true }),
      ...(moduleValue || {}),
    };
  });

  ALWAYS_ON_MODULE_KEYS.forEach((moduleKey) => {
    merged[moduleKey] = {
      ...(merged[moduleKey] || {}),
      enabled: true,
    };
  });

  return merged;
};

const getDateFromUnknown = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDayBoundaryTime = (value) => {
  const date = getDateFromUnknown(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const getDaysUntil = (value) => {
  const targetTime = getDayBoundaryTime(value);
  if (!targetTime) return null;
  const now = new Date();
  const todayTime = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((targetTime - todayTime) / DAY_MS);
};

export const formatShortDate = (value) => {
  const date = getDateFromUnknown(value);
  if (!date) return 'Sin fecha';
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatRemainingDaysLabel = (daysRemaining) => {
  if (typeof daysRemaining !== 'number') return '';
  if (daysRemaining < 0) return 'Vencido';
  if (daysRemaining === 0) return 'Vence hoy';
  if (daysRemaining === 1) return 'Queda 1 día';
  return `Quedan ${daysRemaining} días`;
};

export const resolveBillingCycleLabel = (value = '') => {
  const normalized = normalizeComparableText(value);
  return normalized === 'annual' || normalized === 'anual' ? 'Anual' : 'Mensual';
};

export const getTrialMeta = ({ generalSettings = {}, planSettings = {}, rootData = {} } = {}) => {
  const startedAt =
    getDateFromUnknown(planSettings.trialStartedAt) ||
    getDateFromUnknown(generalSettings.trialStartedAt) ||
    getDateFromUnknown(rootData.trialStartedAt) ||
    getDateFromUnknown(planSettings.createdAt) ||
    getDateFromUnknown(generalSettings.createdAt) ||
    null;

  const endsAt =
    getDateFromUnknown(planSettings.trialEndsAt) ||
    getDateFromUnknown(generalSettings.trialEndsAt) ||
    getDateFromUnknown(rootData.trialEndsAt) ||
    null;

  const currentPlanKey = resolveCompanyPlanMeta({ generalSettings, planSettings }).key;
  const hasHistory = Boolean(startedAt || endsAt || currentPlanKey === 'trial');
  const daysRemaining = getDaysUntil(endsAt);
  const expired = typeof daysRemaining === 'number' ? daysRemaining < 0 : Boolean(endsAt && endsAt.getTime() < Date.now());
  const expiringSoon = !expired && typeof daysRemaining === 'number' && daysRemaining <= TRIAL_EXPIRING_SOON_DAYS;

  return {
    hasTrialHistory: hasHistory,
    startedAt,
    endsAt,
    expired,
    expiringSoon,
    daysRemaining,
    status: !hasHistory ? 'none' : expired ? 'expired' : expiringSoon ? 'expiring' : 'active',
  };
};

export const getBillingMeta = ({ generalSettings = {}, planSettings = {}, rootData = {} } = {}) => {
  const billingCycle =
    planSettings.billingCycle ||
    generalSettings.billingCycle ||
    rootData.billingCycle ||
    'monthly';

  const subscribedAt =
    getDateFromUnknown(planSettings.subscribedAt) ||
    getDateFromUnknown(generalSettings.subscribedAt) ||
    getDateFromUnknown(rootData.subscribedAt) ||
    null;

  const nextBillingAt =
    getDateFromUnknown(planSettings.nextBillingAt) ||
    getDateFromUnknown(generalSettings.nextBillingAt) ||
    getDateFromUnknown(rootData.nextBillingAt) ||
    null;

  const daysRemaining = getDaysUntil(nextBillingAt);
  const expired = typeof daysRemaining === 'number' ? daysRemaining < 0 : false;
  const expiringSoon = !expired && typeof daysRemaining === 'number' && daysRemaining <= BILLING_EXPIRING_SOON_DAYS;
  const hasBillingWindow = Boolean(subscribedAt || nextBillingAt);

  return {
    billingCycle,
    billingCycleLabel: resolveBillingCycleLabel(billingCycle),
    subscribedAt,
    nextBillingAt,
    hasBillingWindow,
    expired,
    expiringSoon,
    daysRemaining,
    status: !hasBillingWindow ? 'none' : expired ? 'expired' : expiringSoon ? 'expiring' : 'active',
  };
};

export const resolveCommercialState = ({ generalSettings = {}, planSettings = {}, modulesSettings = {}, rootData = {} } = {}) => {
  const planMeta = resolveCompanyPlanMeta({ generalSettings, planSettings, modulesSettings });
  const trialMeta = getTrialMeta({ generalSettings, planSettings, rootData });
  const billingMeta = getBillingMeta({ generalSettings, planSettings, rootData });
  const isTrial = planMeta.key === 'trial';

  let status = 'plan_active';
  let tone = 'slate';
  let badge = 'Plan activo';
  let title = `${planMeta.label} en operación`;
  let description = 'Acceso operativo disponible.';
  let emphasisLabel = planMeta.label;
  let nextDate = null;
  let nextDateLabel = '';
  let countdownDays = null;
  let countdownLabel = '';
  let allowOperationalAccess = true;
  let forcePlansPanel = false;

  if (isTrial) {
    nextDate = trialMeta.endsAt;
    nextDateLabel = trialMeta.endsAt ? `Vence el ${formatShortDate(trialMeta.endsAt)}` : '';
    countdownDays = trialMeta.daysRemaining;
    countdownLabel = formatRemainingDaysLabel(trialMeta.daysRemaining);

    if (trialMeta.expired) {
      status = 'trial_expired';
      tone = 'red';
      badge = 'Trial vencido';
      title = 'El período de prueba finalizó';
      description = 'La empresa puede ingresar y conservar sus datos. Los módulos operativos quedan en pausa hasta activar un plan; mantenemos un mes de resguardo comercial para ordenar la continuidad.';
      emphasisLabel = 'Operación pausada';
      allowOperationalAccess = false;
      forcePlansPanel = true;
    } else if (trialMeta.expiringSoon) {
      status = 'trial_expiring';
      tone = 'amber';
      badge = 'Trial por vencer';
      title = 'El período de prueba está por vencer';
      description = 'El trial sigue activo. Conviene definir el próximo plan antes del vencimiento.';
      emphasisLabel = countdownLabel || 'Trial activo';
      forcePlansPanel = true;
    } else {
      status = 'trial_active';
      tone = 'cyan';
      badge = 'Trial activo';
      title = 'El trial sigue operativo';
      description = 'Trial activo dentro del entorno base de G-CENTRA.';
      emphasisLabel = countdownLabel || 'Trial activo';
    }
  } else if (billingMeta.hasBillingWindow) {
    nextDate = billingMeta.nextBillingAt;
    nextDateLabel = billingMeta.nextBillingAt ? `Renueva el ${formatShortDate(billingMeta.nextBillingAt)}` : '';
    countdownDays = billingMeta.daysRemaining;
    countdownLabel = formatRemainingDaysLabel(billingMeta.daysRemaining);

    if (billingMeta.expired) {
      status = 'subscription_expired';
      tone = 'red';
      badge = 'Suscripción vencida';
      title = 'La suscripción venció';
      description = 'La empresa puede ingresar y conservar sus datos. Los módulos operativos quedan en pausa hasta regularizar el plan; mantenemos un mes de resguardo comercial para ordenar la continuidad.';
      emphasisLabel = 'Renovación pendiente';
      allowOperationalAccess = false;
      forcePlansPanel = true;
    } else if (billingMeta.expiringSoon) {
      status = 'subscription_expiring';
      tone = 'amber';
      badge = 'Renovación próxima';
      title = 'La suscripción está próxima a renovarse';
      description = 'La suscripción sigue activa. Conviene revisar la renovación antes del vencimiento.';
      emphasisLabel = countdownLabel || 'Renovación próxima';
    } else {
      status = 'subscription_active';
      tone = 'emerald';
      badge = 'Plan activo';
      title = `${planMeta.label} en operación`;
      description = 'Plan activo con vigencia comercial definida.';
      emphasisLabel = nextDateLabel || planMeta.label;
    }
  } else {
    status = 'plan_active';
    tone = 'emerald';
    badge = 'Plan activo';
    title = `${planMeta.label} en operación`;
    description = 'Plan activo y acceso operativo disponible.';
    emphasisLabel = planMeta.label;
  }

  return {
    planMeta,
    trialMeta,
    billingMeta,
    isTrial,
    isPaidPlan: !isTrial,
    status,
    tone,
    badge,
    title,
    description,
    emphasisLabel,
    nextDate,
    nextDateLabel,
    countdownDays,
    countdownLabel,
    allowOperationalAccess,
    shouldRestrictOperations: !allowOperationalAccess,
    isExpired: status === 'trial_expired' || status === 'subscription_expired',
    isExpiringSoon: status === 'trial_expiring' || status === 'subscription_expiring',
    forcePlansPanel,
    lockedMessage:
      status === 'trial_expired'
        ? 'El trial venció. Podés ingresar y revisar tu espacio, pero los módulos operativos quedan pausados hasta activar un plan.'
        : status === 'subscription_expired'
          ? 'La suscripción venció. Podés ingresar y revisar tu espacio, pero los módulos operativos quedan pausados hasta regularizar el plan.'
          : '',
    helperText:
      status === 'trial_expiring'
        ? 'Conviene definir el próximo plan antes del vencimiento del trial.'
        : status === 'subscription_expiring'
          ? 'Conviene revisar la renovación antes del vencimiento.'
          : '',
  };
};

export const createSynchronizedPlanFields = (planMeta, trialMeta = {}) => {
  const plan = planMeta?.key && PLAN_LOOKUP[planMeta.key] ? PLAN_LOOKUP[planMeta.key] : null;
  const label = plan?.label || planMeta?.label || 'Plan Personalizado';
  const key = plan?.key || planMeta?.key || 'personalizado';

  return {
    currentPlanId: key,
    currentPlanName: label,
    subscriptionPlan: key,
    subscriptionPlanName: label,
    planId: key,
    planKey: key,
    planName: label,
    plan: label,
    trialStartedAt: trialMeta.startedAt || null,
    trialEndsAt: trialMeta.endsAt || null,
    trialStatus: trialMeta.status || 'none',
  };
};

export const buildTrialWindowFromNow = (days = DEFAULT_TRIAL_DAYS) => {
  const startedAt = new Date();
  const endsAt = new Date(startedAt.getTime() + days * DAY_MS);
  return { startedAt, endsAt, status: 'active', daysRemaining: days, hasTrialHistory: true, expired: false };
};
